import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Dialog,
    IconButton,
    CircularProgress,
    TextField,
} from '@mui/material';
import { Close } from '@material-ui/icons';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import loadStripeJs from '../../../../utils/loadStripe';
import { getLearningProgramPlansService } from '../../../../services/learning_programs.service';
import { getStripeConfigService } from '../../../../services_archived/app.service';
import {
    subscribeService,
    getSubscriptionStatusService,
    changePlanService,
} from '../../../../services/billing.service';
import { refreshToken } from '../../../../services/auth.service';
import { toast, toastWarning } from '../../../../utils/utils';
import { TOAST_TYPES } from '../../../../utils/constants';
import { useAuth } from '../../../../context/auth';
import LoaderWithBackDrop from '../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';


// Module-level singleton — Stripe docs mandate loadStripe is called once and never inside
// a render cycle or async handler. Caching here means all PlansDialog mounts reuse the same
// Stripe.js instance and avoid tearing down the iframe on every dialog open.
let _stripePromiseCache = null;

const CARD_CLASSES = ['orangePlan', 'tealPlan', 'bluePlan'];

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#1a1a1a',
            '::placeholder': { color: '#aab7c4' },
        },
        invalid: { color: '#fa755a' },
    },
};

// Convert flat ["# title", "- item"] list to grouped sections
const parseFeatures = (features = []) => {
    // Support both formats from backend:
    // 1) array of lines
    // 2) single multiline string
    const sourceLines = Array.isArray(features)
        ? features.flatMap((entry) =>
            String(entry || '')
                .split(/\r?\n/)
        )
        : String(features || '')
            .split(/\r?\n/);

    const sections = [];
    let currentSection = null;

    sourceLines.forEach((line) => {
        if (typeof line !== 'string') return;
        const normalized = line.trim();
        if (!normalized) return;
        const normalizedBullet = normalized.replace(/^•\s*/, '- ');

        // Accept headings even when prefixed by a bullet marker, e.g. "- # Title"
        const headingMatch = normalizedBullet.match(/^[-*]?\s*#+\s*(.+)$/);
        if (headingMatch) {
            currentSection = {
                title: headingMatch[1].trim(),
                items: [],
            };
            sections.push(currentSection);
        } else if (/^[-*]\s+/.test(normalizedBullet)) {
            if (currentSection) {
                currentSection.items.push(normalizedBullet.replace(/^[-*]\s+/, '').trim());
            }
        } else {
            // Fallback for legacy plain-text items without markers
            if (!currentSection) {
                currentSection = { title: '', items: [] };
                sections.push(currentSection);
            }
            currentSection.items.push(normalizedBullet);
        }
    });

    return sections;
};

// ── Stripe card form ─────────────────────────────────────────────────────────
const SubscribeForm = ({ planLabel, clientSecret, onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardName, setCardName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [cardError, setCardError] = useState('');
    // Synchronous guard — React state updates are async so a double-click can
    // fire confirmCardSetup twice before `submitting` state prevents the second call.
    const submittingRef = useRef(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        // Block immediately — synchronous, unlike setState
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        setCardError('');

        try {
            const cardElement = elements.getElement(CardElement);
            const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: { name: cardName },
                },
            });

            if (error) {
                setCardError(error.message);
                return;
            }

            if (setupIntent?.status === 'succeeded') {
                onSuccess();
            }
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                p: '20px 10px',
                minWidth: '460px',
                maxWidth: '500px',
                margin: '0 auto',
                backgroundColor: '#fff',
                borderRadius: '24px',
            }}
        >
            <Box sx={{ textAlign: 'center', mb: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '24px', color: '#052F44', mb: 1 }}>
                    Subscribe to <span style={{ color: '#F14A24' }}>{planLabel}</span>
                </Typography>
            </Box>

            <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#475569', mb: 1, ml: 0.5 }}>
                    CARDHOLDER NAME
                </Typography>
                <TextField
                    placeholder="e.g. John Doe"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    required
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: '#f8fafc',
                        }
                    }}
                />
            </Box>

            <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#475569', mb: 1, ml: 0.5 }}>
                    CARD DETAILS
                </Typography>
                <Box
                    sx={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        p: '14px',
                        backgroundColor: '#f8fafc',
                        transition: 'border-color 0.2s',
                        '&:focus-within': {
                            borderColor: '#F14A24',
                            boxShadow: '0 0 0 1px #F14A24',
                        }
                    }}
                >
                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                </Box>
            </Box>

            {cardError && (
                <Typography sx={{ 
                    color: '#ef4444', 
                    fontSize: '13px', 
                    textAlign: 'center',
                    backgroundColor: '#fef2f2',
                    p: 1.5,
                    borderRadius: '8px',
                    border: '1px solid #fee2e2'
                }}>
                    {cardError}
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mt: 1 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting || !stripe}
                    fullWidth
                    sx={{ 
                        backgroundColor: '#F14A24', 
                        py: 1.5,
                        borderRadius: '12px',
                        fontWeight: 700,
                        textTransform: 'none',
                        fontSize: '16px',
                        boxShadow: '0 4px 12px rgba(241, 74, 36, 0.2)',
                        '&:hover': { backgroundColor: '#d43a18' } 
                    }}
                >
                    {submitting
                        ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                        : 'Confirm & Subscribe'}
                </Button>
            </Box>
        </Box>
    );
};

// ── Activation poller ────────────────────────────────────────────────────────
const ActivationPoller = ({ onActivated, onTimeout }) => {
    useEffect(() => {
        let cancelled = false;
        let attempts = 0;
        const MAX_ATTEMPTS = 10;
        const INTERVAL_MS = 2000;
        let timeoutId;

        const poll = async () => {
            if (cancelled) return;
            try {
                const res = await getSubscriptionStatusService();
                if (cancelled) return; // Guard: component may have unmounted while awaiting
                if (res?.data?.subscription_status === 'active') {
                    onActivated();
                    return; // Success — don't schedule next poll
                }
            } catch (_) {}
            if (cancelled) return;
            attempts += 1;
            if (attempts >= MAX_ATTEMPTS) {
                onTimeout();
            } else {
                timeoutId = setTimeout(poll, INTERVAL_MS);
            }
        };

        timeoutId = setTimeout(poll, INTERVAL_MS);

        // Cleanup: cancel any pending poll when component unmounts (dialog closed, navigated away)
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', p: '56px' }}>
            <CircularProgress sx={{ color: '#F14A24' }} size={48} />
            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#052F44' }}>
                Activating your subscription…
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#666' }}>This may take a few seconds.</Typography>
        </Box>
    );
};

// ── Main dialog ──────────────────────────────────────────────────────────────
const PlansDialog = ({ open, onClose, onSelectPlan, classes, currentPlanKey: externalCurrentPlanKey, currentPlanTitle: externalCurrentPlanTitle, forceNewSubscription = false }) => {
    const { setAuthToken, setAuthUser } = useAuth();
    const history = useHistory();
    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [stripePromise, setStripePromise] = useState(null);

    // Subscribe flow
    const [subscribingPlan, setSubscribingPlan] = useState(null); // { key, label, clientSecret }
    const [polling, setPolling] = useState(false);
    const [activationTimedOut, setActivationTimedOut] = useState(false);
    const [activatedPlanKey, setActivatedPlanKey] = useState(null);

    // Change-plan flow
    const [changingPlan, setChangingPlan] = useState(null);
    const [currentPlanKey, setCurrentPlanKey] = useState(externalCurrentPlanKey || null);
    const [currentPlanTitle, setCurrentPlanTitle] = useState(externalCurrentPlanTitle || null);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);

    // Synchronous guards — prevent double-clicks from firing duplicate API calls
    // before React state updates (which are async) can prevent them.
    const subscribingRef = useRef(false);
    const changingRef = useRef(false);

    useEffect(() => {
        if (!open) return;

        // Fetch plans and Stripe config independently — either can fail without breaking the other.
        setPlansLoading(true);
        getLearningProgramPlansService()
            .then((plansRes) => {
                if (plansRes?.data?.plans) setPlans(plansRes.data.plans);
                const currentPlan = plansRes?.data?.current_plan;
                if (currentPlan) {
                    const label = currentPlan.plan_label || currentPlan.label;
                    if (label) setCurrentPlanTitle(label);
                    if (!forceNewSubscription) {
                        const actualKey = currentPlan.key || currentPlan.plan_key;
                        if (actualKey) setCurrentPlanKey(actualKey);
                    }
                }
            })
            .catch((err) => console.error('[PlansDialog] plans fetch error:', err))
            .finally(() => setPlansLoading(false));

        // Load Stripe independently so the card form is ready before the user clicks a plan.
        // Use the module-level cache: loadStripe must never be called inside a render cycle
        // or repeated on every dialog open — doing so tears down Stripe's iframe between opens.
        if (!_stripePromiseCache) {
            getStripeConfigService()
                .then((stripeRes) => {
                    if (stripeRes?.data?.publishableKey && !_stripePromiseCache) {
                        _stripePromiseCache = loadStripeJs(stripeRes.data.publishableKey);
                    }
                    setStripePromise(_stripePromiseCache);
                })
                .catch((err) => console.warn('[PlansDialog] Stripe config fetch error:', err));
        } else {
            setStripePromise(_stripePromiseCache);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (externalCurrentPlanKey) setCurrentPlanKey(externalCurrentPlanKey);
    }, [externalCurrentPlanKey]);

    useEffect(() => {
        if (externalCurrentPlanTitle) setCurrentPlanTitle(externalCurrentPlanTitle);
    }, [externalCurrentPlanTitle]);

    const isSubscribed = !forceNewSubscription && !!currentPlanKey && currentPlanKey !== 'default';

    const refreshUser = async () => {
        try {
            const res = await refreshToken();
            if (res?.data?.auth_token) setAuthToken(res.data.auth_token);
            if (res?.data?.user)       setAuthUser(res.data.user);
        } catch (_) {}
    };

    const handleSelectPlan = useCallback(async (plan) => {
        if (isSubscribed) {
            // Change plan
            // Double-click guard: check ref (synchronous) AND state (for UI feedback)
            if (changingRef.current || changingPlan) return;
            changingRef.current = true;
            setChangingPlan(plan.key);
            try {
                const res = await changePlanService(plan.key);
                const data = res?.data;
                if (data?.success) {
                    if (data.is_upgrade) {
                        toast(TOAST_TYPES.SUCCESS, 'Plan Upgraded', 'Your plan has been upgraded. A prorated charge has been applied.');
                    } else {
                        const endDate = data.current_period_end
                            ? new Date(data.current_period_end).toLocaleDateString()
                            : 'your next billing cycle';
                        toast(TOAST_TYPES.SUCCESS, 'Plan Change Scheduled', `Your plan will change to ${plan.label} on ${endDate}.`);
                    }
                    setCurrentPlanKey(plan.key);
                    await refreshUser();
                    if (onSelectPlan) onSelectPlan(plan.key);
                    onClose();
                    history.push('/my_workspace');
                }
            } catch (e) {
                const msg = e?.response?.data?.message || e?.message || 'Failed to change plan. Please try again.';
                toastWarning(msg);
            } finally {
                changingRef.current = false;
                setChangingPlan(null);
            }
        } else {
            // New subscription — request SetupIntent
            // Double-click guard: check ref (synchronous) AND state (subscribingPlan already set)
            if (subscribingRef.current || subscribingPlan) return;
            subscribingRef.current = true;
            try {
                const res = await subscribeService(plan.key);
                const { setup_intent_client_secret, plan_label } = res?.data || {};
                if (!setup_intent_client_secret) throw new Error('No client secret returned.');
                setActivatedPlanKey(plan.key);
                setSubscribingPlan({
                    key: plan.key,
                    label: plan_label || plan.label,
                    clientSecret: setup_intent_client_secret,
                });
            } catch (e) {
                const msg = e?.response?.data?.message || e?.message || 'Failed to initiate subscription. Please try again.';
                toastWarning(msg);
            } finally {
                // Always release the ref so a retry after an error is possible
                subscribingRef.current = false;
            }
        }
    }, [isSubscribed, changingPlan, subscribingPlan, stripePromise, onSelectPlan, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCardSuccess = () => {
        setSubscribingPlan(null);
        setPolling(true);
    };

    const handleActivated = async () => {
        setPolling(false);
        toast(TOAST_TYPES.SUCCESS, 'Subscription Active', 'Your subscription is now active!');
        if (activatedPlanKey) setCurrentPlanKey(activatedPlanKey);
        await refreshUser();
        if (onSelectPlan) onSelectPlan(activatedPlanKey);
        onClose();
        history.push('/my_workspace');
    };

    const handleActivationTimeout = () => {
        setPolling(false);
        setActivationTimedOut(true);
    };

    const handleClose = () => {
        if (polling) return;
        // Reset synchronous guards so a re-open starts clean
        subscribingRef.current = false;
        changingRef.current = false;
        setSubscribingPlan(null);
        setActivationTimedOut(false);
        onClose();
    };

    const renderPlanCards = () => {
        if (plansLoading) return <LoaderWithBackDrop loading={true} />;

        return plans.map((plan, index) => {
            const isCurrent = currentPlanKey === plan.key;
            const isChanging = changingPlan === plan.key;
            const isEnterprisePlan = plan.key === 'enterprise';
            const parsedFeatures = parseFeatures(plan.features);

            return (
                <Box key={plan.key} className={`${classes.planCard} ${classes[CARD_CLASSES[index % CARD_CLASSES.length]]}`}>
                    {plan.recommended && <Box className={classes.recommendedBadge}>Most Capable</Box>}
                    <Typography className={classes.planHeader}>{plan.label}</Typography>
                    <Typography className={classes.planDescription}>{plan.description}</Typography>
                    <Box className={classes.planFeatureList}>
                        {parsedFeatures.map((section, i) => (
                            <Box key={i} sx={{ mb: 1 }}>
                                {section.title && (
                                    <Box component="div" sx={{ fontWeight: 800, fontSize: '16px', mb: 0.5, color: '#1f1f1f' }}>
                                        {section.title}
                                    </Box>
                                )}
                                {section.items.length > 0 && (
                                    <Box component="ul" sx={{ mt: 0, mb: 0, pl: '18px' }}>
                                        {section.items.map((item, j) => (
                                            <Box component="li" key={j} sx={{ mb: 0.25, fontSize: '14px' }}>
                                                {item}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                    {!isEnterprisePlan && (
                        <Typography className={classes.planPrice}>${plan.price_monthly_usd}/month</Typography>
                    )}
                    <Button
                        className={classes.planSelectBtn}
                        variant="contained"
                        disabled={isCurrent || !!changingPlan || plansLoading}
                        onClick={() => {
                            if (isEnterprisePlan) {
                                setContactDialogOpen(true);
                                return;
                            }
                            handleSelectPlan(plan);
                        }}
                        sx={isEnterprisePlan ? {
                            backgroundColor: '#ff1e1e !important',
                            '&:hover': { backgroundColor: '#e01313 !important' }
                        } : {}}
                    >
                        {isChanging
                            ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                            : isCurrent
                                ? 'Current Plan'
                                : isEnterprisePlan
                                    ? 'Contact Us'
                                : isSubscribed
                                    ? 'Switch to This Plan'
                                    : plan.cta_label}
                    </Button>
                </Box>
            );
        });
    };

    const renderContent = () => {
        if (polling) {
            return (
                <ActivationPoller
                    onActivated={handleActivated}
                    onTimeout={handleActivationTimeout}
                />
            );
        }

        if (activationTimedOut) {
            return (
                <Box sx={{ p: '48px', textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#052F44', mb: 1 }}>
                        Subscription is being activated
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#666', mb: 3 }}>
                        This is taking a bit longer than expected. Check back shortly — your subscription will be active soon.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: '#F14A24', '&:hover': { backgroundColor: '#d43a18' } }}
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </Box>
            );
        }

        if (subscribingPlan && stripePromise) {
            return (
                <Elements stripe={stripePromise}>
                    <SubscribeForm
                        planLabel={subscribingPlan.label}
                        clientSecret={subscribingPlan.clientSecret}
                        onSuccess={handleCardSuccess}
                        onCancel={() => setSubscribingPlan(null)}
                    />
                </Elements>
            );
        }

        return (
            <>
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography className={classes.planTitle} sx={{ mb: 1.5 }}>
                        {currentPlanTitle === 'Default Plan' ? 'Upgrade to a Pro ' : 'Choose a '}
                        <span style={{ color: '#F14A24' }}>Plan.</span>
                    </Typography>

                    {currentPlanTitle && (
                        <Box sx={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            backgroundColor: 'rgba(234, 88, 12, 0.08)',
                            padding: '8px 20px',
                            borderRadius: '100px',
                            border: '1px solid rgba(234, 88, 12, 0.2)'
                        }}>
                            <Typography sx={{ 
                                fontSize: '0.8rem', 
                                color: '#64748b', 
                                fontWeight: 600, 
                                textTransform: 'uppercase', 
                                letterSpacing: '0.05em'
                            }}>
                                Current Plan:
                            </Typography>
                            <Typography sx={{ 
                                fontSize: '1rem',
                                color: '#ea580c', 
                                fontWeight: 800 
                            }}>
                                {currentPlanTitle}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box className={classes.planGrid}>
                    {renderPlanCards()}
                </Box>
            </>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            className={classes.plansDialog}
            maxWidth={(polling || subscribingPlan || activationTimedOut) ? "sm" : "lg"}
            fullWidth
        >
            {!polling && (
                <Box sx={{ position: 'absolute', right: 16, top: 16, zIndex: 10 }}>
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Box>
            )}
            {renderContent()}
            <Dialog
                open={contactDialogOpen}
                onClose={() => setContactDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <Box sx={{ p: '28px' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '20px', color: '#052F44', mb: 1 }}>
                        Contact Us
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#666', mb: 2 }}>
                        For enterprise pricing and custom requirements, please contact us at:
                    </Typography>
                    <Typography
                        component="a"
                        href="mailto:contact@dqforall.com"
                        sx={{
                            color: '#F14A24',
                            fontWeight: 700,
                            fontSize: '15px',
                            textDecoration: 'none',
                            mb: 3,
                            display: 'inline-block',
                        }}
                    >
                        contact@dqforall.com
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            onClick={() => setContactDialogOpen(false)}
                            sx={{ backgroundColor: '#F14A24', '&:hover': { backgroundColor: '#d43a18' } }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Dialog>
    );
};

export default PlansDialog;
